import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { AuthShell, AuthIcon } from '../../components/layout/AuthShell';
import { WhatsAppIcon } from '../../components/icons/WhatsAppIcon';
import { Clock, CheckCircle2, LogOut, Phone, Mail, Sparkles, ArrowRight } from 'lucide-react';

const PendingApprovalPage = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <AuthShell
      testId="pending-approval-page"
      eyebrow="Dein Konto"
      title={<>Freischaltung <span className="serif-it text-purple">ausstehend.</span></>}
      intro={user && (<>Angemeldet als: <span className="font-medium text-ink">{user.email}</span></>)}
      maxWidth="max-w-lg"
      below={
        <Link to="/" className="text-purple font-medium hover:underline" data-testid="pending-back-home">
          Zurück zur Startseite
        </Link>
      }
    >
      <AuthIcon tone="amber"><Clock className="w-8 h-8" /></AuthIcon>

      {/* Erstberatung CTA */}
      <div className="sec--purple p-5 mb-6 border-2 border-ink" data-testid="pending-consultation-cta">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 bg-off/15 border-2 border-lilac flex items-center justify-center flex-shrink-0">
            <Sparkles className="w-5 h-5 text-lilac" />
          </div>
          <div className="flex-1">
            <h3 className="font-display font-bold text-lg mb-1">Jetzt Erstberatung starten!</h3>
            <p className="text-off/85 mb-4">
              Nutze die Wartezeit und starte deine kostenlose digitale Erstberatung.
              So können wir direkt nach der Freischaltung loslegen.
            </p>
            <Button asChild variant="lilac" size="sm">
              <Link to="/erstberatung" data-testid="pending-start-consultation">
                Erstberatung starten
                <ArrowRight className="w-4 h-4" />
              </Link>
            </Button>
          </div>
        </div>
      </div>

      <div className="bg-amber-50 border-2 border-amber-300 p-4 mb-6">
        <p className="text-amber-900 text-center">
          Dein Account wird geprüft und kurzfristig freigeschaltet.
          Du erhältst eine E-Mail, sobald dein Zugang aktiviert wurde.
        </p>
      </div>

      <div className="mb-6">
        <h3 className="font-display font-bold text-lg mb-2">Was passiert als nächstes?</h3>
        <ul className="rule-list">
          <li className="flex items-start gap-3">
            <CheckCircle2 className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
            <span className="text-ink/80">E-Mail-Adresse bestätigt</span>
          </li>
          <li className="flex items-start gap-3">
            <Clock className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
            <span className="text-ink/80">Manuelle Prüfung durch unser Team (in Bearbeitung)</span>
          </li>
          <li className="flex items-start gap-3">
            <div className="w-5 h-5 border-2 border-ink mt-0.5 flex-shrink-0" />
            <span className="text-ink/60">Freischaltung und Zugang zur Plattform</span>
          </li>
        </ul>
      </div>

      <div className="bg-off border-2 border-ink p-4 mb-6">
        <p className="text-ink/80 mb-3">Fragen? Kontaktiere uns:</p>
        <div className="flex flex-wrap gap-x-5 gap-y-2">
          <a href="tel:+491787261209" className="flex items-center gap-2 text-purple font-medium hover:underline">
            <Phone className="w-4 h-4" />
            0178 726 1209
          </a>
          <a
            href="https://wa.me/491787261209"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 text-green-700 font-medium hover:underline"
          >
            <WhatsAppIcon size={18} />
            WhatsApp
          </a>
          <a href="mailto:maximilian@gastro-beratung.de" className="flex items-center gap-2 text-purple font-medium hover:underline">
            <Mail className="w-4 h-4" />
            E-Mail
          </a>
        </div>
      </div>

      <Button variant="outline" className="btn--block" onClick={handleLogout} data-testid="logout-button">
        <LogOut className="w-4 h-4" />
        Abmelden
      </Button>
    </AuthShell>
  );
};

export default PendingApprovalPage;
